<template>
  <div class="min-h-screen bg-gray-100 pb-20" v-if="!loading">
    <div class="mx-auto space-y-4" style="width: 100%; max-width: 600px;">
      
      <!-- Arama Bölümü -->
      <div class="px-4 pt-3">
        <div class="relative">
          <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input 
            v-model="searchQuery"
            type="text" 
            placeholder="Ara..."
            class="w-full pl-10 pr-4 py-3 bg-white rounded-xl border border-gray-200 focus:outline-none focus:border-takas-sari focus:ring-2 focus:ring-takas-sari/20 transition"
          />
        </div>
      </div>

      <!-- HEADER - Premium Tasarım -->
      <div class="relative overflow-hidden bg-gradient-to-br from-takas-koyu to-gray-800 rounded-2xl p-5 text-white mx-4">
        <div class="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
        <div class="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>

        <div class="relative z-10">
          <div class="flex items-start justify-between mb-4">
            <div>
              <div class="text-sm text-takas-sari mb-1">Hoş Geldin</div>
              <h1 class="text-2xl font-bold">{{ fullName }}</h1>
            </div>
            
            <div class="flex gap-2">
              <router-link to="/notifications">
                <div class="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg relative">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                  </svg>
                  <span class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">3</span>
                </div>
              </router-link>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div class="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                Aktif İlan: {{ activeListings }}
              </div>
            </div>
            <div class="text-right">
              <div class="text-xs text-takas-sari mb-1">Tamamlanan Takas</div>
              <div class="text-sm font-medium">{{ completedTrades }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Çift Sıra Kaydırmalı Vitrin İlanları (Altlı Üstlü) -->
      <div class="px-4">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-bold text-gray-800">🌟 Vitrin İlanları</h2>
          <router-link to="/vitrin" class="text-xs text-takas-sari font-medium">Tümünü Gör →</router-link>
        </div>
        
        <!-- Çift sıra kaydırma -->
        <div class="overflow-x-auto scrollbar-hide pb-2">
          <div class="flex gap-3" style="min-width: min-content;">
            <!-- Her bir grup 2 ilan (altlı üstlü) -->
            <div v-for="(group, groupIndex) in vitrinGroups" :key="groupIndex" class="flex flex-col gap-2">
              <div v-for="listing in group" :key="listing.id" 
                   @click="goToListing(listing.id)"
                   class="w-36 bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition">
                <div class="h-20 bg-gradient-to-br from-takas-sari/30 to-yellow-400/30 flex items-center justify-center">
                  <span class="text-2xl">{{ listing.icon }}</span>
                </div>
                <div class="p-1.5">
                  <h3 class="font-semibold text-xs text-gray-800 truncate">{{ listing.title }}</h3>
                  <p class="text-[10px] text-gray-500">{{ listing.category }}</p>
                  <p class="text-[10px] text-takas-sari font-medium">Teklif Et</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Yeni İlanlar -->
      <div class="px-4">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-bold text-gray-800">🆕 Yeni İlanlar</h2>
          <router-link to="/yeni-ilanlar" class="text-xs text-takas-sari font-medium">Tümünü Gör →</router-link>
        </div>
        <div class="space-y-2">
          <div v-for="listing in newListings" :key="listing.id" 
               @click="goToListing(listing.id)"
               class="bg-white rounded-xl p-3 flex gap-3 cursor-pointer hover:shadow-md transition">
            <div class="w-14 h-14 bg-gradient-to-br from-takas-sari/30 to-yellow-400/30 rounded-lg flex items-center justify-center">
              <span class="text-2xl">{{ listing.icon }}</span>
            </div>
            <div class="flex-1">
              <div class="flex justify-between">
                <h3 class="font-semibold text-sm">{{ listing.title }}</h3>
                <span class="text-xs text-green-600">{{ listing.price || 'Takaslık' }}</span>
              </div>
              <p class="text-xs text-gray-500">{{ listing.category }} • {{ listing.location }}</p>
              <div class="flex items-center gap-2 mt-1">
                <span class="text-[10px] text-gray-400">{{ listing.timeAgo }}</span>
                <span class="text-[10px] text-takas-sari">🔁 {{ listing.offerCount }} teklif</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- En Çok Teklif Alanlar -->
      <div class="px-4">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-bold text-gray-800">🔥 En Çok Teklif Alanlar</h2>
          <router-link to="/populer" class="text-xs text-takas-sari font-medium">Tümünü Gör →</router-link>
        </div>
        <div class="overflow-x-auto scrollbar-hide">
          <div class="flex gap-3">
            <div v-for="listing in popularListings" :key="listing.id" 
                 @click="goToListing(listing.id)"
                 class="flex-shrink-0 w-32 bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition">
              <div class="h-24 bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center relative">
                <span class="text-3xl">{{ listing.icon }}</span>
                <div class="absolute top-1 right-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  +{{ listing.offerCount }}
                </div>
              </div>
              <div class="p-2">
                <h3 class="font-semibold text-xs text-gray-800 truncate">{{ listing.title }}</h3>
                <p class="text-[10px] text-gray-500">{{ listing.category }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sana Yakın İlanlar (Konum bazlı) -->
      <div class="px-4">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-bold text-gray-800">📍 Sana Yakın</h2>
          <router-link to="/yakinda" class="text-xs text-takas-sari font-medium">Tümünü Gör →</router-link>
        </div>
        <div class="space-y-2">
          <div v-for="listing in nearbyListings" :key="listing.id" 
               @click="goToListing(listing.id)"
               class="bg-white rounded-xl p-3 flex gap-3 cursor-pointer hover:shadow-md transition">
            <div class="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
              <span class="text-2xl">{{ listing.icon }}</span>
            </div>
            <div class="flex-1">
              <div class="flex justify-between">
                <h3 class="font-semibold text-sm">{{ listing.title }}</h3>
                <span class="text-xs text-blue-600">{{ listing.distance }}</span>
              </div>
              <p class="text-xs text-gray-500">{{ listing.category }} • {{ listing.location }}</p>
              <div class="flex items-center gap-2 mt-1">
                <span class="text-[10px] text-gray-400">{{ listing.timeAgo }}</span>
                <span class="text-[10px] text-takas-sari">🔁 {{ listing.offerCount }} teklif</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- En Çok Takas Yapan Kullanıcılar -->
      <div class="px-4 pb-4">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-bold text-gray-800">🏆 Takas Liderleri </h2>
          <router-link to="/leaders" class="text-xs text-takas-sari font-medium">Tümünü Gör →</router-link>
        </div>
        <div class="overflow-x-auto scrollbar-hide">
          <div class="flex gap-3">
            <div v-for="user in topTraders" :key="user.id" 
                 @click="goToProfile(user.id)"
                 class="flex-shrink-0 w-28 bg-white rounded-xl p-3 text-center cursor-pointer hover:shadow-md transition">
              <div class="w-14 h-14 rounded-full bg-gradient-to-br from-takas-sari to-yellow-400 flex items-center justify-center mx-auto">
                <span class="text-2xl font-bold text-takas-koyu">{{ user.initial }}</span>
              </div>
              <h3 class="font-semibold text-xs mt-2">{{ user.name }}</h3>
              <p class="text-[10px] text-takas-sari font-medium">{{ user.tradeCount }} takas</p>
              <div class="flex items-center justify-center gap-0.5 mt-1">
                <svg v-for="star in user.rating" :key="star" class="w-3 h-3 text-takas-sari" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
                <span class="text-[10px] text-gray-500">({{ user.totalRatings }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Kategori Listesi (3'lü Grid) -->
      <div class="px-4 pb-4">
        <h2 class="text-lg font-bold text-gray-800 mb-3">📁 Kategoriler</h2>
        <div class="grid grid-cols-3 gap-3">
          <div 
            v-for="category in filteredCategories" 
            :key="category.id"
            @click="goToCategory(category)"
            class="bg-white rounded-xl p-3 cursor-pointer hover:shadow-md transition text-center"
          >
            <div class="w-12 h-12 bg-gradient-to-br from-takas-sari to-yellow-400 rounded-xl flex items-center justify-center mx-auto mb-2">
              <span class="text-2xl">{{ category.icon }}</span>
            </div>
            <h3 class="font-medium text-xs text-gray-800">{{ category.name }}</h3>
            <p class="text-[10px] text-gray-400 mt-1">{{ category.count }} ilan</p>
          </div>
        </div>
      </div>

    </div>
  </div>
  
  <div v-else class="min-h-screen flex items-center justify-center">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-takas-sari mx-auto"></div>
      <p class="mt-4 text-gray-600">Yükleniyor...</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  data() {
    return {
      loading: false,
      searchQuery: '',
      fullName: '',
      activeListings: 1243,
      completedTrades: 892,
      vitrinListings: [
        { id: 1, title: 'iPhone 15 Pro', category: 'Telefon', icon: '📱' },
        { id: 2, title: 'MacBook Air M2', category: 'Bilgisayar', icon: '💻' },
        { id: 3, title: 'PlayStation 5', category: 'Oyun', icon: '🎮' },
        { id: 4, title: 'Samsung TV', category: 'Elektronik', icon: '📺' },
        { id: 5, title: 'İngilizce Kursu', category: 'Hizmet', icon: '📚' },
        { id: 6, title: 'AirPods Pro', category: 'Aksesuar', icon: '🎧' },
        { id: 7, title: 'Nintendo Switch', category: 'Oyun', icon: '🎮' },
        { id: 8, title: 'DSLR Kamera', category: 'Fotoğraf', icon: '📷' }
      ],
      newListings: [
        { id: 101, title: 'iPhone 14 Pro Max', category: 'Telefon', location: 'İstanbul', price: 'Takaslık', icon: '📱', timeAgo: '5 dk önce', offerCount: 3 },
        { id: 102, title: 'Xbox Series S', category: 'Oyun', location: 'Ankara', price: 'Takas+500₺', icon: '🎮', timeAgo: '15 dk önce', offerCount: 7 },
        { id: 103, title: 'Samsung Buds2', category: 'Aksesuar', location: 'İzmir', price: 'Takaslık', icon: '🎧', timeAgo: '1 saat önce', offerCount: 2 }
      ],
      popularListings: [
        { id: 201, title: 'iPhone 13', category: 'Telefon', icon: '📱', offerCount: 28 },
        { id: 202, title: 'MacBook Pro', category: 'Bilgisayar', icon: '💻', offerCount: 23 },
        { id: 203, title: 'PlayStation 4', category: 'Oyun', icon: '🎮', offerCount: 19 },
        { id: 204, title: 'Samsung S22', category: 'Telefon', icon: '📱', offerCount: 15 }
      ],
      nearbyListings: [
        { id: 301, title: 'Ikea Masa', category: 'Mobilya', location: 'Kadıköy', distance: '1.2 km', icon: '🪑', timeAgo: '2 saat önce', offerCount: 5 },
        { id: 302, title: 'Yoga Dersi', category: 'Hizmet', location: 'Beşiktaş', distance: '2.5 km', icon: '🧘', timeAgo: '3 saat önce', offerCount: 4 },
        { id: 303, title: 'Ders Kitapları', category: 'Kitap', location: 'Üsküdar', distance: '3 km', icon: '📚', timeAgo: '5 saat önce', offerCount: 2 }
      ],
      topTraders: [
        { id: 401, name: 'Ahmet Y.', initial: 'A', tradeCount: 156, rating: 5, totalRatings: 234 },
        { id: 402, name: 'Mehmet D.', initial: 'M', tradeCount: 128, rating: 5, totalRatings: 189 },
        { id: 403, name: 'Ayşe K.', initial: 'A', tradeCount: 97, rating: 5, totalRatings: 145 },
        { id: 404, name: 'Can Ö.', initial: 'C', tradeCount: 84, rating: 4, totalRatings: 112 }
      ],
      categories: [
        { id: 1, name: 'Antika', icon: '🏺', count: 45 },
        { id: 2, name: 'Anne & Bebek', icon: '👶', count: 234 },
        { id: 3, name: 'Beyaz Eşya', icon: '🧺', count: 89 },
        { id: 4, name: 'Bilgisayar', icon: '💻', count: 567 },
        { id: 5, name: 'Tablet', icon: '📱', count: 123 },
        { id: 6, name: 'Telefon', icon: '📞', count: 891 },
        { id: 7, name: 'Diğer Elektronik', icon: '⚡', count: 234 },
        { id: 8, name: 'Ev & Bahçe', icon: '🏠', count: 456 },
        { id: 9, name: 'Giyim', icon: '👕', count: 1234 },
        { id: 10, name: 'Hizmet', icon: '💼', count: 345 },
        { id: 11, name: 'Kitap & Dergi', icon: '📚', count: 678 },
        { id: 12, name: 'Kırtasiye', icon: '✏️', count: 123 },
        { id: 13, name: 'Kişisel Bakım', icon: '💄', count: 234 },
        { id: 14, name: 'Koleksiyon', icon: '🃏', count: 89 },
        { id: 15, name: 'Mobilya', icon: '🪑', count: 345 },
        { id: 16, name: 'Müzik Aletleri', icon: '🎸', count: 156 },
        { id: 17, name: 'Oto Aksesuar', icon: '🚗', count: 267 },
        { id: 18, name: 'Oyun & Konsol', icon: '🎮', count: 178 },
        { id: 19, name: 'Outdoor & Kamp', icon: '⛺', count: 89 },
        { id: 20, name: 'Sanat & El İşi', icon: '🎨', count: 123 },
        { id: 21, name: 'Spor', icon: '⚽', count: 234 },
        { id: 22, name: 'Telefon Aksesuar', icon: '📱', count: 456 }
      ]
    }
  },
  computed: {
    filteredCategories() {
      if (!this.searchQuery) return this.categories
      return this.categories.filter(cat => 
        cat.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    },
    vitrinGroups() {
      const groups = []
      for (let i = 0; i < this.vitrinListings.length; i += 2) {
        groups.push(this.vitrinListings.slice(i, i + 2))
      }
      return groups
    }
  },
  created() {
    const user = localStorage.getItem('user')
    if (user) {
      const userData = JSON.parse(user)
      this.fullName = userData.name || 'Misafir'
    } else {
      this.fullName = 'Misafir'
    }
  },
  methods: {
    goToCategory(category) {
      this.$router.push({
        name: 'CategoryDetail',
        params: { categoryName: category.name }
      })
    },
    goToListing(id) {
      this.$router.push(`/listing/${id}`)
    },
    goToProfile(id) {
      this.$router.push(`/profile/${id}`)
    }
  }
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>